const CUSTOMIZABLE_TYPES = /* @__PURE__ */ new Set(["text", "rating", "single", "multi", "emotion", "likert"]);
const TEXT_VARIANTS = [
  { value: "text", label: "Plain text" },
  { value: "email", label: "Email" },
  { value: "url", label: "URL" },
  { value: "tel", label: "Phone" },
  { value: "number", label: "Number" }
];
const RATING_STYLES = [
  { value: "numbers", label: "Numbers", preview: "1 2 3 4 5" },
  { value: "stars", label: "Stars", preview: "\u2605\u2605\u2605\u2605\u2605" },
  { value: "hearts", label: "Hearts", preview: "\u2665\u2665\u2665\u2665\u2665" },
  { value: "emojis", label: "Emoji faces", preview: "\u{1F61E} \u{1F610} \u{1F642} \u{1F600} \u{1F929}" }
];
const RATING_SCALES = [5, 10];
const RATING_EMOJI_SETS = {
  5: ["\u{1F61E}", "\u{1F610}", "\u{1F642}", "\u{1F600}", "\u{1F929}"],
  10: ["\u{1F621}", "\u{1F620}", "\u{1F61E}", "\u{1F615}", "\u{1F610}", "\u{1F642}", "\u{1F600}", "\u{1F604}", "\u{1F929}", "\u{1F973}"]
};
// Likert matrix scales. Preset names and the 5-point defaults must mirror the
// server (question_bank.py LIKERT_SCALE_PRESETS); non-default point sets are
// stored explicitly in config.scale_points as [{key,label}].
const LIKERT_SCALE_PRESETS = {
  agreement: { label: "Agreement (Strongly disagree → Strongly agree)", counts: [3, 5, 7] },
  satisfaction: { label: "Satisfaction (Very dissatisfied → Very satisfied)", counts: [3, 5, 7] },
  frequency: { label: "Frequency (Never → Always)", counts: [3, 5] },
  numeric: { label: "Numeric rating (1–N)", counts: [5, 10] }
};
const LIKERT_POINT_SETS = {
  agreement: {
    3: [
      { key: "disagree", label: "Disagree" },
      { key: "neutral", label: "Neutral" },
      { key: "agree", label: "Agree" }
    ],
    5: [
      { key: "strongly_disagree", label: "Strongly disagree" },
      { key: "disagree", label: "Disagree" },
      { key: "neutral", label: "Neutral" },
      { key: "agree", label: "Agree" },
      { key: "strongly_agree", label: "Strongly agree" }
    ],
    7: [
      { key: "strongly_disagree", label: "Strongly disagree" },
      { key: "disagree", label: "Disagree" },
      { key: "somewhat_disagree", label: "Somewhat disagree" },
      { key: "neutral", label: "Neutral" },
      { key: "somewhat_agree", label: "Somewhat agree" },
      { key: "agree", label: "Agree" },
      { key: "strongly_agree", label: "Strongly agree" }
    ]
  },
  satisfaction: {
    3: [
      { key: "dissatisfied", label: "Dissatisfied" },
      { key: "neutral", label: "Neutral" },
      { key: "satisfied", label: "Satisfied" }
    ],
    5: [
      { key: "very_dissatisfied", label: "Very dissatisfied" },
      { key: "dissatisfied", label: "Dissatisfied" },
      { key: "neutral", label: "Neutral" },
      { key: "satisfied", label: "Satisfied" },
      { key: "very_satisfied", label: "Very satisfied" }
    ],
    7: [
      { key: "very_dissatisfied", label: "Very dissatisfied" },
      { key: "dissatisfied", label: "Dissatisfied" },
      { key: "somewhat_dissatisfied", label: "Somewhat dissatisfied" },
      { key: "neutral", label: "Neutral" },
      { key: "somewhat_satisfied", label: "Somewhat satisfied" },
      { key: "satisfied", label: "Satisfied" },
      { key: "very_satisfied", label: "Very satisfied" }
    ]
  },
  frequency: {
    3: [
      { key: "never", label: "Never" },
      { key: "sometimes", label: "Sometimes" },
      { key: "always", label: "Always" }
    ],
    5: [
      { key: "never", label: "Never" },
      { key: "rarely", label: "Rarely" },
      { key: "sometimes", label: "Sometimes" },
      { key: "often", label: "Often" },
      { key: "always", label: "Always" }
    ]
  }
};
function buildLikertPoints(preset, count) {
  if (preset === "numeric") {
    const n = count === 10 ? 10 : 5;
    return Array.from({ length: n }, (_, i) => ({ key: String(i + 1), label: String(i + 1) }));
  }
  const sets = LIKERT_POINT_SETS[preset] || LIKERT_POINT_SETS.agreement;
  return (sets[count] || sets[5]).map((p) => ({ ...p }));
}
const EMOTION_PRESETS = {
  "5_sentiment": {
    label: "5-point sentiment (Very negative \u2192 Very positive)",
    options: [
      { key: "very_negative", label: "Very negative" },
      { key: "negative", label: "Negative" },
      { key: "neutral", label: "Neutral" },
      { key: "positive", label: "Positive" },
      { key: "very_positive", label: "Very positive" }
    ]
  },
  "3_sentiment": {
    label: "3-point sentiment (Negative / Neutral / Positive)",
    options: [
      { key: "negative", label: "Negative" },
      { key: "neutral", label: "Neutral" },
      { key: "positive", label: "Positive" }
    ]
  },
  thumbs: {
    label: "Thumbs up / down",
    options: [
      { key: "down", label: "\u{1F44E}" },
      { key: "up", label: "\u{1F44D}" }
    ]
  },
  yes_no: {
    label: "Yes / No",
    options: [
      { key: "yes", label: "Yes" },
      { key: "no", label: "No" }
    ]
  }
};
// Predefined suggestion sets shown to a respondent who gives a LOW rating,
// grouped by "area of interest". A survey creator enables suggestions on a
// rating question, picks a set here, and may then hand-edit the labels.
// EDIT THESE FREELY: add/remove sets or options; each option needs a unique
// `key` (stable id, stored in responses) and a `label` (what respondents see).
const LOW_RATING_SUGGESTION_SETS = {
  facilities: {
    label: "Facilities & Workplace",
    options: [
      { key: "cleanliness", label: "Cleanliness" },
      { key: "odor", label: "Unpleasant smell / odor" },
      { key: "temperature", label: "Temperature (too hot / too cold)" },
      { key: "lighting", label: "Lighting" },
      { key: "noise", label: "Noise levels" },
      { key: "maintenance", label: "Maintenance or repairs needed" },
      { key: "restroom", label: "Restroom condition" }
    ]
  },
  cleanliness: {
    label: "Cleanliness & Hygiene",
    options: [
      { key: "floors", label: "Floors / carpets" },
      { key: "restrooms", label: "Restrooms" },
      { key: "surfaces", label: "Desks / surfaces" },
      { key: "trash", label: "Trash not emptied" },
      { key: "pantry", label: "Kitchen / pantry area" },
      { key: "supplies", label: "Supplies not restocked" }
    ]
  },
  catering: {
    label: "Food & Beverage / Catering",
    options: [
      { key: "taste", label: "Food quality / taste" },
      { key: "freshness", label: "Freshness" },
      { key: "variety", label: "Variety / choice" },
      { key: "portion", label: "Portion size" },
      { key: "food_temp", label: "Temperature of food" },
      { key: "wait", label: "Wait time / service speed" },
      { key: "dining_clean", label: "Cleanliness of dining area" }
    ]
  },
  it: {
    label: "IT & Technology",
    options: [
      { key: "wifi", label: "Wi-Fi / connectivity" },
      { key: "performance", label: "Slow performance" },
      { key: "access", label: "Login / access issues" },
      { key: "av", label: "Audio-visual / conferencing" },
      { key: "hardware", label: "Hardware / equipment" },
      { key: "software", label: "Software / application errors" },
      { key: "support", label: "Support response time" }
    ]
  },
  meetings: {
    label: "Meeting Rooms & Events",
    options: [
      { key: "booking", label: "Room availability / booking" },
      { key: "av_equip", label: "AV / conferencing equipment" },
      { key: "room_clean", label: "Room cleanliness" },
      { key: "seating", label: "Seating / capacity" },
      { key: "comfort", label: "Temperature / comfort" },
      { key: "refreshments", label: "Catering / refreshments" },
      { key: "logistics", label: "Organization / logistics" }
    ]
  },
  staff: {
    label: "Staff & Service",
    options: [
      { key: "responsiveness", label: "Responsiveness" },
      { key: "attitude", label: "Friendliness / attitude" },
      { key: "knowledge", label: "Knowledge / helpfulness" },
      { key: "resolution", label: "Wait / resolution time" },
      { key: "communication", label: "Communication" },
      { key: "professionalism", label: "Professionalism" }
    ]
  },
  amenities: {
    label: "Amenities & Comfort",
    options: [
      { key: "seating_comfort", label: "Seating comfort" },
      { key: "lounge", label: "Break / lounge areas" },
      { key: "parking", label: "Parking" },
      { key: "accessibility", label: "Accessibility" },
      { key: "air", label: "Air quality / ventilation" },
      { key: "crowding", label: "Crowding / space" }
    ]
  },
  general: {
    label: "General (all-purpose)",
    options: [
      { key: "quality", label: "Quality" },
      { key: "timeliness", label: "Timeliness / speed" },
      { key: "communication", label: "Communication" },
      { key: "value", label: "Value for money" },
      { key: "ease", label: "Ease of use" },
      { key: "service", label: "Staff / service" }
    ]
  }
};
function asString(v) {
  return typeof v === "string" && v ? v : void 0;
}
function asInt(v) {
  return typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : void 0;
}
function getTextVariant(cfg) {
  const v = asString(cfg?.variant);
  return v && (TEXT_VARIANTS.find((t) => t.value === v)?.value ?? "text") || "text";
}
function getRatingScale(cfg) {
  const n = asInt(cfg?.scale);
  return n === 10 ? 10 : 5;
}
function getRatingStyle(cfg) {
  const v = asString(cfg?.style);
  return v && (RATING_STYLES.find((t) => t.value === v)?.value ?? "numbers") || "numbers";
}
function getRatingFollowUp(cfg) {
  return cfg?.rating_follow_up === true;
}
function getLowRatingEnabled(cfg) {
  return cfg?.low_rating === true;
}
function getLowRatingThreshold(cfg) {
  const n = asInt(cfg?.low_rating_threshold);
  return n && n >= 1 ? n : 3;
}
function getLowRatingAllowOther(cfg) {
  return cfg?.low_rating_hide_other !== true;
}
function getLowRatingPreset(cfg) {
  return asString(cfg?.low_rating_preset);
}
function getLowRatingOptions(cfg) {
  const arr = Array.isArray(cfg?.low_rating_options) ? cfg.low_rating_options : [];
  return arr
    .map((o) => ({ key: asString(o?.key) ?? "", label: asString(o?.label) ?? "" }))
    .filter((o) => o.key && o.label);
}
function getLikertPreset(cfg) {
  const v = asString(cfg?.scale_preset);
  return v && LIKERT_SCALE_PRESETS[v] ? v : "agreement";
}
function getLikertScalePoints(cfg) {
  const arr = Array.isArray(cfg?.scale_points) ? cfg.scale_points : [];
  const points = arr
    .map((p) => ({ key: asString(p?.key) ?? "", label: asString(p?.label) ?? "" }))
    .filter((p) => p.key && p.label);
  if (points.length >= 2) return points;
  return buildLikertPoints(getLikertPreset(cfg), 5);
}
function getLikertUniqueColumns(cfg) {
  return cfg?.unique_columns === true;
}
function getMultiChoiceDisplay(cfg) {
  return asString(cfg?.choice_display) === "dropdown" ? "dropdown" : "checkboxes";
}
function getSingleChoiceDisplay(cfg) {
  return cfg?.choice_display === "dropdown" ? "dropdown" : "standard";
}
function getPlaceholder(cfg) {
  return asString(cfg?.placeholder);
}
// Mirrors the server's required-answer rule (survey_public.py _validate_answers)
// closely enough to gate wizard "Next" client-side; the server has final say.
function isQuestionAnswered(q, answers) {
  const v = answers[q.question_key];
  if (q.type === "multi") {
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === "object" && Array.isArray(v.values)) return v.values.length > 0;
    return false;
  }
  if ((q.type === "single" || q.type === "dropdown") && v && typeof v === "object" && !Array.isArray(v)) {
    return typeof v.value === "string" && v.value.trim().length > 0;
  }
  if (q.type === "likert") {
    // Matrix answer: {row_option_key: point_key}. Required means every
    // statement row is rated (server enforces the same via likert_partial).
    if (!v || typeof v !== "object" || Array.isArray(v)) return false;
    const rows = q.options || [];
    return rows.length > 0 && rows.every((o) => typeof v[o.option_key] === "string" && v[o.option_key]);
  }
  return typeof v === "string" && v.trim().length > 0;
}
// `visible` (a Set of question_keys, from branchingPath.js) is optional — when
// given, only questions on the respondent's current branching path count, so
// a required question skipped by branching doesn't block "Next".
function missingRequiredKeys(section, answers, visible) {
  return (section?.questions || [])
    .filter((q) => (!visible || visible.has(q.question_key)) && q.required && !isQuestionAnswered(q, answers))
    .map((q) => q.question_key);
}
function isSectionComplete(section, answers, visible) {
  return missingRequiredKeys(section, answers, visible).length === 0;
}
function getTextareaRows(_cfg) {
  return 4;
}
function getMaxLength(cfg) {
  const n = asInt(cfg?.max_length);
  return n && n > 0 ? n : void 0;
}
function getMinLength(cfg) {
  const n = asInt(cfg?.min_length);
  return n && n > 0 ? n : void 0;
}
function getNumber(cfg, key) {
  const v = cfg?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : void 0;
}
function questionConfigSummary(qtype, cfg) {
  if (!cfg) return "";
  const parts = [];
  if (qtype === "text") {
    const variant = getTextVariant(cfg);
    if (variant !== "text") parts.push(TEXT_VARIANTS.find((v) => v.value === variant)?.label || variant);
    const ml = getMaxLength(cfg);
    if (ml) parts.push(`max ${ml} chars`);
  } else if (qtype === "rating") {
    const scale = getRatingScale(cfg);
    if (scale !== 5) parts.push(`1\u2013${scale}`);
    const style = getRatingStyle(cfg);
    if (style !== "numbers") parts.push(RATING_STYLES.find((s) => s.value === style)?.label || style);
    if (getRatingFollowUp(cfg)) parts.push("optional follow-up reason");
    if (getLowRatingEnabled(cfg)) parts.push(`low-rating suggestions (≤${getLowRatingThreshold(cfg)})`);
  } else if (qtype === "single") {
    if (getSingleChoiceDisplay(cfg) === "dropdown") parts.push("dropdown list");
  } else if (qtype === "multi") {
    if (getMultiChoiceDisplay(cfg) === "dropdown") parts.push("dropdown list");
  } else if (qtype === "emotion") {
    const preset = asString(cfg.preset);
    if (preset && EMOTION_PRESETS[preset]) {
      parts.push(EMOTION_PRESETS[preset].label);
    }
  } else if (qtype === "likert") {
    const preset = getLikertPreset(cfg);
    const points = getLikertScalePoints(cfg);
    parts.push(`${points.length}-point ${preset} scale`);
    if (getLikertUniqueColumns(cfg)) parts.push("each value used once");
  }
  return parts.join(" \xB7 ");
}
export {
  CUSTOMIZABLE_TYPES,
  EMOTION_PRESETS,
  LIKERT_SCALE_PRESETS,
  LOW_RATING_SUGGESTION_SETS,
  RATING_EMOJI_SETS,
  RATING_SCALES,
  RATING_STYLES,
  TEXT_VARIANTS,
  buildLikertPoints,
  getLikertPreset,
  getLikertScalePoints,
  getLikertUniqueColumns,
  getLowRatingAllowOther,
  getLowRatingEnabled,
  getLowRatingOptions,
  getLowRatingPreset,
  getLowRatingThreshold,
  getMaxLength,
  getMinLength,
  getMultiChoiceDisplay,
  getSingleChoiceDisplay,
  getNumber,
  getPlaceholder,
  getRatingFollowUp,
  getRatingScale,
  getRatingStyle,
  getTextVariant,
  getTextareaRows,
  isQuestionAnswered,
  isSectionComplete,
  missingRequiredKeys,
  questionConfigSummary
};
